import React, { useEffect, useState } from "react";
import { Megaphone, Gavel, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../lib/api";
import { useLang } from "../lib/i18n";
import { printDoc } from "../lib/printDoc";
import { itemPhotoUrl } from "../pages/shop/Items";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const EMPTY_BID = { bidder_name: "", bidder_id: "", phone: "", amount: "" };

function BidderRegister({ lots, onSold }) {
  const { t } = useLang();
  const [bids, setBids] = useState({});
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => API.get("/shop/auctions/bids").then((r) => setBids(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const form = (id) => forms[id] || EMPTY_BID;
  const setF = (id, k, v) => setForms((f) => ({ ...f, [id]: { ...form(id), [k]: v } }));
  const add = async (lot) => {
    const f = form(lot.id);
    if (!f.bidder_name || !f.amount) return;
    setBusy(true);
    try {
      await API.post(`/shop/items/${lot.id}/bids`, { ...f, amount: Number(f.amount), bidder_id: f.bidder_id || null, phone: f.phone || null });
      setForms((x) => ({ ...x, [lot.id]: EMPTY_BID }));
      toast.success(t("bid.recorded"));
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await API.delete(`/shop/bids/${id}`); load(); } catch (err) { toast.error(fmtErr(err)); } };
  const award = async (lot) => {
    const top = bids[lot.id]?.[0];
    if (!top || !window.confirm(`${t("bid.award")} ${top.bidder_name} — ${money(top.amount)}?`)) return;
    setBusy(true);
    try {
      await API.post(`/shop/items/${lot.id}/award`);
      toast.success(t("msg.sold"));
      load(); onSold && onSold(lot.id);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="bidder-register">
      <h4 className="font-heading font-medium text-sm flex items-center gap-2 mb-2"><Gavel className="h-4 w-4 text-purple-600" />{t("bid.title")}</h4>
      <div className="space-y-3">
        {lots.map((lot) => {
          const list = bids[lot.id] || [];
          const top = list[0];
          const belowReserve = top && top.amount < lot.reserve_price;
          return (
            <div key={lot.id} className="border border-slate-200 rounded-lg p-3" data-testid={`bid-lot-${lot.lot}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
                <span className="font-mono text-slate-500">#{lot.lot}</span><span className="font-medium flex-1">{lot.name}</span>
                <span className="text-slate-500">{t("ann.reserve")}: <span className="font-mono">{money(lot.reserve_price)}</span></span>
                {top && <span className={`font-semibold px-2 py-0.5 rounded-full ${belowReserve ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`bid-top-${lot.lot}`}>{t("bid.highest")}: {money(top.amount)} · {top.bidder_name}</span>}
                <Button size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white" disabled={!top || belowReserve || busy} onClick={() => award(lot)} data-testid={`bid-award-${lot.lot}`}>{t("bid.award")}</Button>
              </div>
              {list.length > 0 && (
                <div className="text-xs divide-y divide-slate-100 mb-2" data-testid={`bid-list-${lot.lot}`}>
                  {list.map((b, i) => (
                    <div key={b.id} className="flex items-center gap-2 py-1">
                      <span className="w-5 text-slate-400">{i + 1}.</span>
                      <span className="flex-1">{b.bidder_name}{b.bidder_id ? ` · ID ${b.bidder_id}` : ""}{b.phone ? ` · ${b.phone}` : ""}</span>
                      <span className="font-mono font-semibold">{money(b.amount)}</span>
                      <span className="text-slate-400">{b.created_at?.slice(11, 16)}</span>
                      <button onClick={() => remove(b.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="h-3 w-3 text-red-600" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Input data-testid={`bid-name-${lot.lot}`} placeholder={t("bid.name")} value={form(lot.id).bidder_name} onChange={(e) => setF(lot.id, "bidder_name", e.target.value)} className="h-8 text-xs col-span-2 sm:col-span-1" />
                <Input placeholder={t("ct.party_id")} value={form(lot.id).bidder_id} onChange={(e) => setF(lot.id, "bidder_id", e.target.value)} className="h-8 text-xs" />
                <Input placeholder={t("common.phone")} value={form(lot.id).phone} onChange={(e) => setF(lot.id, "phone", e.target.value)} className="h-8 text-xs" />
                <Input data-testid={`bid-amount-${lot.lot}`} type="number" min="0" step="0.01" placeholder={t("bid.amount")} value={form(lot.id).amount} onChange={(e) => setF(lot.id, "amount", e.target.value)} className="h-8 text-xs" />
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={() => add(lot)} data-testid={`bid-add-${lot.lot}`}>{t("bid.add")}</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnnouncementDoc({ ann, printMode }) {
  const { t } = useLang();
  const shop = ann.shop;
  return (
    <div className={`print-area bg-white text-sm ${printMode === "thermal" ? "thermal-doc" : ""}`} data-testid="announcement-body">
      <div className="flex items-center gap-4 border-b-2 border-[var(--brand,#1E3A5F)] pb-3">
        <div>
          <div className="font-heading font-bold text-xl text-[var(--brand,#1E3A5F)] uppercase">{shop.name}</div>
          <div className="text-xs text-slate-500">{shop.company_address}{shop.company_phone ? ` · ${shop.company_phone}` : ""}</div>
        </div>
        <div className="ml-auto text-right text-xs font-mono text-slate-500">{ann.announcement_no}<br />{ann.date}</div>
      </div>
      <h2 className="text-center font-heading font-bold text-2xl tracking-wide mt-5" data-testid="announcement-title">{t("ann.title")}</h2>
      <div className="text-center text-sm mt-2">
        <span className="font-semibold">{t("ann.sale_date")}:</span> <span className="font-mono" data-testid="announcement-sale-date">{ann.sale_date || "____________"}</span>
        <span className="mx-3">·</span>
        <span className="font-semibold">{t("ann.location")}:</span> {ann.location || "____________"}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        {ann.lots.map((l) => (
          <div key={l.id} className="border border-slate-300 rounded-lg p-3 flex gap-3" data-testid={`announcement-lot-${l.lot}`}>
            {l.photo_path ? (
              <img src={itemPhotoUrl(l)} alt="" className="h-24 w-24 rounded object-cover border border-slate-200 shrink-0" />
            ) : (
              <div className="h-24 w-24 rounded bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-[10px] text-slate-400">No photo</div>
            )}
            <div className="text-xs flex-1">
              <div className="font-bold text-[var(--brand,#1E3A5F)]">{t("ann.lot")} {l.lot} — {l.name}</div>
              <div className="text-slate-600">{[l.category, l.brand, l.model, l.year, l.color, l.plate_no, l.jewelry_type, l.gold_karat, l.weight_grams ? `${l.weight_grams} g` : null, l.serial_no].filter(Boolean).join(" · ")}</div>
              {l.condition && <div className="text-slate-500 mt-0.5">{l.condition}</div>}
              <div className="mt-1.5 flex justify-between">
                <span className="text-slate-500">{t("ann.est")}: <span className="font-mono">{money(l.estimated_value)}</span></span>
                <span className="font-semibold">{t("ann.reserve")}: <span className="font-mono" data-testid={`announcement-reserve-${l.lot}`}>{money(l.reserve_price)}</span></span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3 text-sm font-semibold">{t("ann.total_reserve")}: <span className="font-mono ml-2">{money(ann.total_reserve)}</span></div>
      <p className="text-[11px] text-slate-600 mt-4 border-t border-dashed border-slate-300 pt-2 leading-relaxed">{t("ann.legal")}</p>
    </div>
  );
}

export function SaleAnnouncement({ managerUp, onAwarded }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [ann, setAnn] = useState(null);
  const [saleDate, setSaleDate] = useState("");
  const [location, setLocation] = useState("");
  const [printMode, setPrintMode] = useState("a4");
  const [reserveEdit, setReserveEdit] = useState({});

  const load = (d = saleDate, loc = location) => API.get("/shop/auctions/announcement", { params: { sale_date: d || undefined, location: loc || undefined } })
    .then((r) => setAnn(r.data)).catch((err) => toast.error(fmtErr(err)));
  const openDialog = () => { setOpen(true); load(); };
  const saveReserve = async (lot) => {
    const v = reserveEdit[lot.id];
    if (v === undefined || v === "") return;
    try {
      await API.patch(`/shop/items/${lot.id}/reserve`, { reserve_price: Number(v) });
      toast.success(t("msg.saved"));
      setReserveEdit((r) => ({ ...r, [lot.id]: undefined }));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <>
      <Button variant="outline" data-testid="announcement-button" onClick={openDialog}>
        <Megaphone className="h-4 w-4 mr-1.5" />{t("ann.button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="announcement-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("ann.button")}</DialogTitle></DialogHeader>
          {ann && (
            <>
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                  <Label className="text-xs">{t("ann.sale_date")}</Label>
                  <Input type="date" data-testid="announcement-date-input" value={saleDate} onChange={(e) => { setSaleDate(e.target.value); load(e.target.value, location); }} className="mt-1 h-9" />
                </div>
                <div className="flex-1 min-w-48">
                  <Label className="text-xs">{t("ann.location")}</Label>
                  <Input data-testid="announcement-location-input" value={location} onChange={(e) => setLocation(e.target.value)} onBlur={() => load(saleDate, location)} className="mt-1 h-9" />
                </div>
              </div>
              {ann.lots.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center" data-testid="announcement-empty">{t("ann.empty")}</p>
              ) : (
                <>
                  {managerUp && (
                    <div className="mb-3 border border-slate-200 rounded-lg divide-y text-xs" data-testid="announcement-reserve-editor">
                      {ann.lots.map((l) => (
                        <div key={l.id} className="flex items-center gap-2 p-2">
                          <span className="font-mono text-slate-500 w-12">#{l.lot}</span>
                          <span className="flex-1 truncate">{l.name}</span>
                          <span className="text-slate-400">{l.reserve_is_default ? `(${t("ann.default_reserve")})` : ""}</span>
                          <Input type="number" min="0" step="0.01" data-testid={`reserve-input-${l.lot}`} placeholder={String(l.reserve_price)}
                            value={reserveEdit[l.id] ?? ""} onChange={(e) => setReserveEdit((r) => ({ ...r, [l.id]: e.target.value }))} className="h-8 w-28 text-xs" />
                          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`reserve-save-${l.lot}`} onClick={() => saveReserve(l)}>{t("ann.set_reserve")}</Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <AnnouncementDoc ann={ann} printMode={printMode} />
                  {managerUp && <BidderRegister lots={ann.lots} onSold={(id) => { load(); onAwarded && onAwarded(id); }} />}
                </>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <div className="flex rounded-lg border border-slate-300 overflow-hidden text-xs">
                  <button onClick={() => setPrintMode("a4")} className={`px-3 py-1.5 ${printMode === "a4" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_a4")}</button>
                  <button onClick={() => setPrintMode("thermal")} className={`px-3 py-1.5 ${printMode === "thermal" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{t("print.mode_thermal")}</button>
                </div>
                <Button data-testid="announcement-print-button" disabled={!ann.lots.length} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                  onClick={() => printDoc(document.querySelector('[data-testid="announcement-body"]'), { mode: printMode, title: ann.announcement_no })}>
                  {t("repay.print")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
